import React, { useEffect, useState } from 'react';
import { auth, firestore } from '../firebase/config';
import { doc, getDoc, setDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from '../components/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatComponent from '../components/ChatComponent';
import Notes from '../components/Notes';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getStudentInvoices } from '../utils/invoiceUtils';
import { getPakistanTime, getPakistanDate, calculateDuration } from '../utils/timeUtils';

function daysSince(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const now = new Date();
  date.setHours(0,0,0,0);
  now.setHours(0,0,0,0);
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  return diff;
}

function StudentDashboard() {
  console.log('StudentDashboard component mounted');
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceToday, setAttendanceToday] = useState(null);
  // Add attendance history state
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const navigate = useNavigate();
  const today = getPakistanDate();
  const [courseOutlines, setCourseOutlines] = useState([]);
  const [courseLoading, setCourseLoading] = useState(true);
  // Add state for checked outlines
  const [checkedOutlines, setCheckedOutlines] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('attendance');
  
  // Invoice search and pagination states
  const [studentInvoices, setStudentInvoices] = useState([]);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [currentInvoicePage, setCurrentInvoicePage] = useState(1);
  const [invoicesPerPage] = useState(5);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Quiz statistics states
  const [quizStats, setQuizStats] = useState({
    totalQuizzes: 0,
    attemptedQuizzes: 0,
    passedQuizzes: 0,
    failedQuizzes: 0
  });
  const [quizResults, setQuizResults] = useState([]);
  const [quizStatsLoading, setQuizStatsLoading] = useState(false);

  // Function to debug database contents
  const debugDatabase = async () => {
    if (!student?.id) return;
    
    console.log('=== DATABASE DEBUG ===');
    console.log('Student ID:', student.id);
    console.log('Student Course:', student.course);
    
    try {
      // Check all quiz results for this student
      const allResultsQuery = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', student.id)
      );
      const allResultsSnapshot = await getDocs(allResultsQuery);
      
      console.log('All quiz results for student:', allResultsSnapshot.docs.length);
      allResultsSnapshot.docs.forEach((doc, index) => {
        console.log(`Result ${index + 1}:`, doc.data());
      });
      
      // Check all quizzes for this course
      const allQuizzesQuery = query(collection(firestore, 'quizzes'));
      const allQuizzesSnapshot = await getDocs(allQuizzesQuery);
      
      console.log('All quizzes in database:', allQuizzesSnapshot.docs.length);
      allQuizzesSnapshot.docs.forEach((doc, index) => {
        const data = doc.data();
        if (data.courseName === student.course || data.course === student.course) {
          console.log(`Quiz ${index + 1} for this course:`, data);
        }
      });
      
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  // Function to fetch attendance history
  const fetchAttendanceHistory = async (uid) => {
    const q = query(collection(firestore, 'studentAttendance'), where('uid', '==', uid));
    const querySnapshot = await getDocs(q);
    const history = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.date < b.date ? 1 : -1);
    setAttendanceHistory(history);
  };

  // Add this function:
  const fetchTodayAttendance = async (uid) => {
    const today = getPakistanDate();
    const todayRef = doc(firestore, 'studentAttendance', `${uid}_${today}`);
    const snap = await getDoc(todayRef);
    if (snap.exists()) {
      setAttendanceToday(snap.data());
    } else {
      setAttendanceToday(null);
    }
  };

  // Function to fetch quiz statistics
  const fetchQuizStats = async (uid) => {
    if (!uid || !student?.course) return;
    
    setQuizStatsLoading(true);
    try {
      console.log('Fetching quiz stats for:', { uid, course: student.course });
      
      // Get all quizzes for this course
      const quizzesQuery = query(
        collection(firestore, 'quizzes'), 
        where('courseName', '==', student.course)
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);
      
      console.log('Quizzes found:', quizzesSnapshot.docs.length);
      quizzesSnapshot.docs.forEach(doc => {
        console.log('Quiz data:', doc.data());
      });
      
      // Also check for quizzes with different field names
      const allQuizzesQuery = query(collection(firestore, 'quizzes'));
      const allQuizzesSnapshot = await getDocs(allQuizzesQuery);
      const allQuizzes = allQuizzesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(quiz => 
          quiz.courseName === student.course || 
          quiz.course === student.course ||
          quiz.courseId === student.course
        );
      
      console.log('All quizzes for course:', allQuizzes.length);
      allQuizzes.forEach(quiz => {
        console.log('All quiz data:', quiz);
      });
      
      // Use the combined results
      const allQuestions = [...quizzesSnapshot.docs, ...allQuizzes]
        .filter((item, index, self) => 
          // Remove duplicates based on document ID
          index === self.findIndex(q => q.id === item.id)
        );
      
      // Group quizzes by quiz name to count unique quizzes
      const quizNames = new Set();
      allQuestions.forEach(quiz => {
        if (quiz.quizName) {
          quizNames.add(quiz.quizName);
        }
      });
      const totalQuizzes = quizNames.size;
      console.log('Unique quiz names:', Array.from(quizNames));

      // Get student's quiz results
      const resultsQuery = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', uid),
        where('courseName', '==', student.course)
      );
      const resultsSnapshot = await getDocs(resultsQuery);
      
      console.log('Quiz results found:', resultsSnapshot.docs.length);
      resultsSnapshot.docs.forEach(doc => {
        console.log('Result data:', doc.data());
      });
      
      // Also check for results with different field names
      const allResultsQuery = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', uid)
      );
      const allResultsSnapshot = await getDocs(allResultsQuery);
      console.log('All quiz results for student:', allResultsSnapshot.docs.length);
      allResultsSnapshot.docs.forEach(doc => {
        console.log('All result data:', doc.data());
      });
      
      // Combine results from both queries to catch any field name mismatches
      const results = [...resultsSnapshot.docs, ...allResultsSnapshot.docs]
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((result, index, self) => 
          // Remove duplicates based on document ID
          index === self.findIndex(r => r.id === result.id)
        );
      setQuizResults(results);

      // Calculate statistics
      const attemptedQuizzes = results.length;
      const passedQuizzes = results.filter(result => result.isPassed).length;
      const failedQuizzes = attemptedQuizzes - passedQuizzes;

      console.log('Calculated stats:', {
        totalQuizzes,
        attemptedQuizzes,
        passedQuizzes,
        failedQuizzes
      });

      setQuizStats({
        totalQuizzes,
        attemptedQuizzes,
        passedQuizzes,
        failedQuizzes
      });
    } catch (error) {
      console.error('Error fetching quiz stats:', error);
      showError('Failed to load quiz statistics');
    }
    setQuizStatsLoading(false);
  };

  // Fetch attendance history on mount and when student changes
  useEffect(() => {
    if (student && student.id) {
      fetchAttendanceHistory(student.id);
      fetchQuizStats(student.id);
    }
  }, [student]);

  // Refresh quiz stats when component becomes visible (e.g., returning from quiz)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && student && student.id) {
        fetchQuizStats(student.id);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [student]);

  // Refresh quiz stats when component mounts or when returning from quiz page
  useEffect(() => {
    if (student && student.id) {
      // Small delay to ensure student data is loaded
      const timer = setTimeout(() => {
        fetchQuizStats(student.id);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [student]);

  useEffect(() => {
    const fetchStudent = async () => {
      // Check for custom authentication first
      const customUser = localStorage.getItem('customUser');
      const customRole = localStorage.getItem('userRole');
      
      console.log('StudentDashboard: Checking custom auth:', { customUser: !!customUser, customRole });
      
      let userUid = null;
      
      if (customUser && customRole) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
        console.log('StudentDashboard: Using custom auth, UID:', userUid);
      } else {
        // Fallback to Firebase Auth
        const user = auth.currentUser;
        if (!user) {
          console.log('StudentDashboard: No user found, redirecting to login');
          navigate('/login');
          return;
        }
        userUid = user.uid;
        console.log('StudentDashboard: Using Firebase auth, UID:', userUid);
      }
      
      try {
        const studentDoc = await getDoc(doc(firestore, "students", userUid));
        if (studentDoc.exists()) {
          setStudent({ id: userUid, ...studentDoc.data() });
        } else {
          setStudent(null);
        }
      } catch (err) {
        console.error('Error fetching student data:', err);
      }
      setLoading(false);
    };
    
    fetchStudent();
  }, [navigate]);

  useEffect(() => {
    const fetchTodayAttendance = async () => {
      if (!student || !student.id) return;
      const today = getPakistanDate();
      const todayRef = doc(firestore, 'studentAttendance', `${student.id}_${today}`);
      const snap = await getDoc(todayRef);
      if (snap.exists()) {
        setAttendanceToday(snap.data());
      } else {
        setAttendanceToday(null);
      }
    };
    fetchTodayAttendance();
  }, [student]);

  useEffect(() => {
    const fetchCourseOutlines = async () => {
      setCourseLoading(true);
      if (student && student.courseName) {
        const q = query(collection(firestore, "courses"), where("name", "==", student.courseName));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const courseDoc = snapshot.docs[0];
          setCourseOutlines(courseDoc.data().outlines || []);
        } else {
          setCourseOutlines([]);
        }
      }
      setCourseLoading(false);
    };
    fetchCourseOutlines();
  }, [student]);

  // Load checked outlines for this course/student
  useEffect(() => {
    if (student && student.checkedOutlines && student.courseName) {
      setCheckedOutlines(student.checkedOutlines[student.courseName] || []);
    } else {
      setCheckedOutlines([]);
    }
  }, [student, student?.courseName]);

  // Fetch invoices when student data is loaded
  useEffect(() => {
    if (student && student.id) {
      console.log('StudentDashboard: Fetching invoices for student:', student.id);
      fetchStudentInvoice();
    }
  }, [student]);

  // Show invoices when invoice tab is active
  useEffect(() => {
    if (activeTab === 'invoice') {
      console.log('StudentDashboard: Invoice tab activated, setting showInvoice to true');
      setShowInvoice(true);
      // Also fetch invoices when tab is activated
      if (student && student.id) {
        console.log('StudentDashboard: Fetching invoices on tab activation');
        fetchStudentInvoice();
      }
    }
  }, [activeTab, student]);

  const fetchStudentInvoice = async () => {
    try {
      setInvoiceLoading(true);
      const customUser = localStorage.getItem('customUser');
      let userUid;
      
      if (customUser) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
      } else {
        const user = auth.currentUser;
        if (!user) return;
        userUid = user.uid;
      }
      
      console.log('StudentDashboard: Fetching invoices for UID:', userUid);
      console.log('StudentDashboard: Student object:', student);
      console.log('StudentDashboard: Student ID from student object:', student?.id);
      console.log('StudentDashboard: Student UID from student object:', student?.uid);
      console.log('StudentDashboard: All student object keys:', Object.keys(student || {}));
      console.log('StudentDashboard: Student document ID (should match userUid):', student?.id);
      console.log('StudentDashboard: Student UID field:', student?.uid);
      
      // Try fetching with the UID from localStorage first (this is what students use)
      let invoices = await getStudentInvoices(userUid);
      console.log('StudentDashboard: Received invoices with UID from localStorage:', invoices.length, invoices);
      
      // If no invoices found, try with the student's document ID as fallback
      if (invoices.length === 0) {
        console.log('StudentDashboard: No invoices found with UID, trying with student document ID');
        invoices = await getStudentInvoices(student?.id);
        console.log('StudentDashboard: Received invoices with student ID:', invoices.length, invoices);
      }
      
      // If still no invoices found, try with student's uid field
      if (invoices.length === 0 && student?.uid) {
        console.log('StudentDashboard: No invoices found with student ID, trying with student uid field');
        invoices = await getStudentInvoices(student?.uid);
        console.log('StudentDashboard: Received invoices with student uid field:', invoices.length, invoices);
      }
      
      // Additional debugging: Check if userUid and student.id are the same
      console.log('StudentDashboard: userUid vs student.id comparison:', {
        userUid,
        studentId: student?.id,
        areEqual: userUid === student?.id
      });
      setStudentInvoices(invoices);
      
      // Set the most recent invoice as the main invoice
      if (invoices.length > 0) {
        setInvoice(invoices[0]);
        console.log('StudentDashboard: Set main invoice:', invoices[0]);
      } else {
        console.log('StudentDashboard: No invoices found');
      }
    } catch (error) {
      console.error('Error fetching student invoices:', error);
      showError('Error loading invoices');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckInLoading(true);
    try {
      // Get user UID from custom auth or Firebase auth
      const customUser = localStorage.getItem('customUser');
      let userUid;
      
      if (customUser) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
      } else {
        const user = auth.currentUser;
        if (!user) {
          showError('User not authenticated');
          setCheckInLoading(false);
          return;
        }
        userUid = user.uid;
      }
      
      // Use Pakistan Standard Time with 12-hour format
      const time = getPakistanTime();
      const date = getPakistanDate();
      const ref = doc(firestore, 'studentAttendance', `${userUid}_${date}`);
      await setDoc(ref, { uid: userUid, date: date, checkIn: time, userType: 'student' });
      setAttendanceToday({ uid: userUid, date: date, checkIn: time });
      await fetchTodayAttendance(student.id);
      await fetchAttendanceHistory(student.id); // Refetch history after check-in
      showSuccess(`Checked in at ${time}`);
    } catch (err) {
      showError('Check-in failed. Try again.');
    }
    setCheckInLoading(false);
  };

  const handleCheckOut = async () => {
    setCheckOutLoading(true);
    try {
      // Get user UID from custom auth or Firebase auth
      const customUser = localStorage.getItem('customUser');
      let userUid;
      
      if (customUser) {
        const parsedUser = JSON.parse(customUser);
        userUid = parsedUser.uid;
      } else {
        const user = auth.currentUser;
        if (!user) {
          showError('User not authenticated');
          setCheckOutLoading(false);
          return;
        }
        userUid = user.uid;
      }
      
      // Use Pakistan Standard Time with 12-hour format
      const time = getPakistanTime();
      const date = getPakistanDate();
      
      // Calculate duration with improved logic for 12-hour format
      let duration = '';
      if (attendanceToday && attendanceToday.checkIn) {
        duration = calculateDuration(attendanceToday.checkIn, time);
      }
      
      const ref = doc(firestore, 'studentAttendance', `${userUid}_${date}`);
      await setDoc(ref, { ...attendanceToday, checkOut: time, duration, userType: 'student' });
      setAttendanceToday(prev => ({ ...prev, checkOut: time, duration }));
      await fetchTodayAttendance(student.id);
      await fetchAttendanceHistory(student.id); // Refetch history after check-out
      showSuccess(`Checked out at ${time}`);
    } catch (err) {
      showError('Check-out failed. Try again.');
    }
    setCheckOutLoading(false);
  };



  // Handler for checking an outline
  const handleCheckOutline = async (idx) => {
    if (!student || !student.id) return; // Prevent error if student is not loaded
    if (checkedOutlines.includes(idx)) return;
    const newChecked = [...checkedOutlines, idx];
    setCheckedOutlines(newChecked);

    // Update Firestore
    const updatedCheckedOutlines = {
      ...(student.checkedOutlines || {}),
      [student.courseName]: newChecked,
    };
    await updateDoc(doc(firestore, "students", student.id), {
      checkedOutlines: updatedCheckedOutlines,
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!student) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">Student data not found.</div>;
  }

  const hasCheckedIn = attendanceToday && attendanceToday.checkIn;
  const hasCheckedOut = attendanceToday && attendanceToday.checkOut;

  // Only show as today's attendance if the date matches today
  const isTodayAttendance = attendanceToday && attendanceToday.date === today;
  const canCheckIn = !isTodayAttendance || !attendanceToday.checkIn;
  const canCheckOut = isTodayAttendance && attendanceToday.checkIn && !attendanceToday.checkOut;

  // Debug logging
  console.log('StudentDashboard Debug:', {
    today: today,
    attendanceToday: attendanceToday,
    attendanceTodayDate: attendanceToday?.date,
    isTodayAttendance,
    canCheckIn,
    canCheckOut
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-blue-50 to-orange-50 flex flex-col">
      {/* Chat Component for Students */}
      <ChatComponent userRole="student" userId={student?.id} userName={student?.fullName} />
      {/* Navbar/Header with Enhanced Animations */}
      <header className="w-full bg-gradient-to-r from-orange-600 via-blue-600 to-orange-600 shadow-2xl py-3 md:py-4 px-3 md:px-6 flex items-center justify-between sticky top-0 z-20 rounded-b-3xl animate-fade-in overflow-hidden relative">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 via-blue-400/20 to-orange-400/20 animate-pulse"></div>
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-400 via-blue-400 to-orange-400 animate-pulse"></div>
        
        <div className="flex items-center gap-2 md:gap-3 animate-slide-in-left relative z-10">
          <div className="relative">
            <img src="/DSH.png" alt="Logo" className="w-8 h-8 md:w-12 md:h-12 rounded-xl shadow-lg animate-pulse" />
            <div className="absolute -top-1 -right-1 w-3 h-3 md:w-4 md:h-4 bg-green-400 rounded-full animate-ping"></div>
          </div>
          <div className="flex flex-col">
            <span className="text-lg md:text-2xl lg:text-3xl font-bold text-white tracking-wide animate-fade-in-delay drop-shadow-lg">
              Digital Skills House
            </span>
            <span className="text-xs md:text-sm text-blue-100 font-medium animate-fade-in-delay-2">
              Student Portal
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 animate-slide-in-right relative z-10">
          <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2 md:px-4 py-1 md:py-2 animate-fade-in-delay-2">
            <span className="text-sm md:text-lg font-semibold text-white hidden sm:block">
              Welcome, {student.fullName}
            </span>
            <span className="text-xs font-semibold text-white sm:hidden">
              {student.fullName?.split(' ')[0]}
            </span>
          </div>
          <button
            onClick={async () => { 
              // Clear custom authentication
              localStorage.removeItem('customUser');
              localStorage.removeItem('userRole');
              
              // Sign out from Firebase Auth
              await signOut(auth); 
              navigate('/login'); 
            }}
            className="flex items-center px-2 md:px-4 py-1 md:py-2 bg-red-500/90 backdrop-blur-sm text-white rounded-lg shadow-lg hover:bg-red-600 transition-all duration-300 transform hover:scale-105 animate-fade-in-delay-3 border border-red-400/50"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
            </svg>
            <span className="text-sm md:text-base">Logout</span>
          </button>
        </div>
      </header>
      <main className="flex-1 w-full max-w-6xl mx-auto py-8 px-2 md:px-8">
        {/* Admin Message */}
        {student?.message && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-xl shadow mb-6 relative mx-auto max-w-md">
            <button
              onClick={async () => {
                try {
                  const user = auth.currentUser;
                  const studentDocRef = doc(firestore, 'students', user.uid);
                  await updateDoc(studentDocRef, { message: null });
                  setStudent(prev => ({ ...prev, message: null }));
                } catch (err) {
                  console.error('Error dismissing message:', err);
                }
              }}
              className="absolute top-2 right-2 text-yellow-600 hover:text-yellow-800 font-bold text-2xl leading-none"
              aria-label="Dismiss"
            >
              ×
            </button>
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <div className="flex-1">
                <div className="font-semibold mb-1">Admin Message</div>
                <div className="whitespace-pre-line">{student.message}</div>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - All Portal Content */}
          <div className="lg:w-2/3">
            {/* Combined Dashboard and Tabs Section */}
            <div className="bg-white/80 rounded-2xl shadow-xl p-6 mb-8">
        {/* Dashboard Summary Section */}
              <div className="flex flex-col md:flex-row items-center gap-8 mb-6">
          <div className="flex flex-col items-center md:items-start flex-1">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-2 shadow">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="text-xl font-bold text-gray-800">{student.fullName}</div>
            <div className="text-gray-500 text-sm">Admission: {student.admissionDate || '-'}</div>
            <div className="text-blue-600 font-semibold flex items-center mt-1">
              <svg className="w-5 h-5 mr-1 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {student.courseName}
            </div>
          </div>
          <div className="flex-1 flex flex-row justify-center gap-4">
            <div className="flex flex-col items-center bg-blue-50 rounded-xl px-6 py-4 shadow">
              <span className="text-xs text-gray-600 mb-1">Total Fee</span>
              <span className="text-xl font-bold text-blue-700">{student.totalFee}</span>
            </div>
            <div className="flex flex-col items-center bg-green-50 rounded-xl px-6 py-4 shadow">
              <span className="text-xs text-gray-600 mb-1">Paid Fee</span>
              <span className="text-xl font-bold text-green-700">{student.submittedFee}</span>
            </div>
            <div className="flex flex-col items-center bg-red-50 rounded-xl px-6 py-4 shadow">
              <span className="text-xs text-gray-600 mb-1">Pending Fee</span>
              <span className="text-xl font-bold text-red-700">{student.pendingFee}</span>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
              <div>
          <div className="flex gap-2 mb-4">
            <button onClick={() => setActiveTab('attendance')} className={`px-2 py-1 rounded-lg font-semibold transition ${activeTab === 'attendance' ? 'bg-gradient-to-r from-orange-600 to-blue-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}>Attendance</button>
            <button onClick={() => setActiveTab('outlines')} className={`px-2 py-2 rounded-lg font-semibold transition ${activeTab === 'outlines' ? 'bg-gradient-to-r from-orange-600 to-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}>Outlines</button>
            <button onClick={() => navigate('/quiz')} className="px-4 py-2 rounded-lg font-semibold transition bg-gradient-to-r from-orange-500 to-blue-500 text-white hover:from-orange-600 hover:to-blue-600">Quiz</button>
            <button onClick={() => setActiveTab('invoice')} className={`px-2 py-2 rounded-lg font-semibold transition ${activeTab === 'invoice' ? 'bg-gradient-to-r from-orange-600 to-blue-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'}`}>Invoice</button>
          </div>
          {activeTab === 'attendance' && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Today's Attendance ({today})</h3>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex gap-4">
                {canCheckIn && (
                  <button onClick={handleCheckIn} className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition">
                    Check In
                  </button>
                )}
                      </div>
                      <div className="flex gap-4">
                {canCheckOut && (
                  <button onClick={handleCheckOut} className="px-6 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 transition">
                    Check Out
                  </button>
                )}
                      </div>
                    </div>
                {isTodayAttendance && attendanceToday.checkIn && attendanceToday.checkOut && (
                      <div className="text-green-600 font-semibold mt-2 text-center">
                    You have completed check-in and check-out for today.
                  </div>
                )}
              <div className="mt-2">
                <span className="font-semibold">Check In:</span> {isTodayAttendance && attendanceToday.checkIn ? attendanceToday.checkIn : '--'}<br />
                <span className="font-semibold">Check Out:</span> {isTodayAttendance && attendanceToday.checkOut ? attendanceToday.checkOut : '--'}
              </div>
              <div className="mt-6">
                <h3 className="text-xl font-semibold mb-4">Attendance History</h3>
                {attendanceHistory.length === 0 ? (
                  <p className="text-gray-600">No records found.</p>
                ) : (
                  <ul className="space-y-2">
                    <li className="flex font-semibold text-gray-700">
                      <span className="w-32">Date</span>
                      <span className="w-40">Check-in</span>
                      <span className="w-40">Check-out</span>
                      <span className="w-32">Duration</span>
                    </li>
                    {attendanceHistory.map((entry) => (
                      <li key={entry.id || entry.date} className="bg-gray-50 p-3 rounded shadow text-left flex">
                        <span className="w-32">{entry.date}</span>
                        <span className="w-40">{entry.checkIn ? `✅ ${entry.checkIn}` : '❌ No Check-in'}</span>
                        <span className="w-40">{entry.checkOut ? `🕓 ${entry.checkOut}` : '⏳ No Check-out'}</span>
                        <span className="w-32">{entry.duration || '-'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          {activeTab === 'outlines' && (
            <div>
              <h3 className="text-lg font-semibold text-purple-700 mb-3">Outlines for this course</h3>
              {courseLoading ? (
                <div>Loading course outlines...</div>
              ) : (
                student && student.id ? (
                  <ol className="space-y-2 list-decimal list-inside">
                    {courseOutlines.length === 0 && <li className="text-gray-400">No outlines for this course.</li>}
                    {courseOutlines.map((outline, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="font-semibold text-purple-700">{idx + 1}.</span>
                        <input
                          type="checkbox"
                          checked={checkedOutlines.includes(idx)}
                          disabled={checkedOutlines.includes(idx)}
                          onChange={() => handleCheckOutline(idx)}
                          className="accent-purple-600"
                        />
                        <span
                          className={`text-gray-800 text-sm ${
                            checkedOutlines.includes(idx) ? "line-through text-gray-400" : ""
                          }`}
                        >
                          {outline}
                        </span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div>Loading student data...</div>
                )
              )}
            </div>
          )}
          {activeTab === 'quiz' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-orange-700">Quiz Statistics</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => student && student.id && fetchQuizStats(student.id)}
                    className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg hover:bg-orange-200 transition-colors text-sm"
                    title="Refresh Quiz Statistics"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                  <button
                    onClick={() => debugDatabase()}
                    className="bg-red-100 text-red-700 px-3 py-1 rounded-lg hover:bg-red-200 transition-colors text-sm"
                    title="Debug Database"
                  >
                    Debug
                  </button>
                </div>
              </div>
              {quizStatsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="w-8 h-8 animate-spin text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Quiz Statistics Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{quizStats.totalQuizzes}</div>
                      <div className="text-sm text-blue-700">Total Quizzes</div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-orange-600">{quizStats.attemptedQuizzes}</div>
                      <div className="text-sm text-orange-700">Attempted</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{quizStats.passedQuizzes}</div>
                      <div className="text-sm text-green-700">Passed</div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{quizStats.failedQuizzes}</div>
                      <div className="text-sm text-red-700">Failed</div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Progress</span>
                      <span className="text-sm text-gray-600">
                        {quizStats.totalQuizzes > 0 ? Math.round((quizStats.attemptedQuizzes / quizStats.totalQuizzes) * 100) : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${quizStats.totalQuizzes > 0 ? (quizStats.attemptedQuizzes / quizStats.totalQuizzes) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Take Quiz Button */}
                  <div className="text-center">
                    <button 
                      onClick={() => navigate('/quiz')} 
                      className="bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 transition-colors font-semibold"
                    >
                      Take Quiz
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'invoice' && (
            <div>
                    {console.log('StudentDashboard: Rendering invoice tab, showInvoice:', showInvoice, 'studentInvoices:', studentInvoices.length)}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-blue-700">Your Fee Invoices</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowInvoice(v => { if (!v) fetchStudentInvoice(); return !v; }); }}
                    className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-full shadow hover:bg-blue-700 transition"
                          title={showInvoice ? 'Hide Invoices' : 'Show Invoices'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 2v2M15 2v2M4 7h16M4 22h16M4 7v15a2 2 0 002 2h8a2 2 0 002-2V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <button
                    onClick={async () => {
                            if (!selectedInvoice) return;
                      const card = document.getElementById('student-invoice-card');
                      if (!card) return;
                      const canvas = await html2canvas(card);
                      const imgData = canvas.toDataURL('image/png');
                      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
                      const pageWidth = pdf.internal.pageSize.getWidth();
                      const imgWidth = pageWidth - 40;
                      const imgHeight = (canvas.height * imgWidth) / canvas.width;
                      pdf.addImage(imgData, 'PNG', 20, 20, imgWidth, imgHeight);
                            pdf.save(`Invoice-${selectedInvoice.invoiceNo || 'DSH'}.pdf`);
                    }}
                    className="w-9 h-9 flex items-center justify-center bg-green-600 text-white rounded-full shadow hover:bg-green-700 transition"
                    title="Download Invoice PDF"
                          disabled={!selectedInvoice}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              </div>
                    
                    {/* Search Bar */}
                    {showInvoice && (
                      <div className="mb-4">
                        <input
                          type="text"
                          placeholder="Search invoices by invoice number..."
                          value={invoiceSearchTerm}
                          onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    )}
                    
              {showInvoice && (
                invoiceLoading ? (
                        <div className="text-center py-8">Loading invoices...</div>
                      ) : studentInvoices.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No invoices found for your account.</div>
                      ) : (
                        <div>
                          {/* Invoice List */}
                          <div className="mb-4">
                            <h4 className="text-md font-semibold mb-2 text-gray-700">Your Invoices</h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {studentInvoices
                                .filter(inv => 
                                  inv.invoiceNo.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                                  inv.date.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                                )
                                .slice((currentInvoicePage - 1) * invoicesPerPage, currentInvoicePage * invoicesPerPage)
                                .map((inv, index) => (
                                  <div
                                    key={inv.id}
                                    onClick={() => setSelectedInvoice(inv)}
                                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                      selectedInvoice?.id === inv.id 
                                        ? 'bg-blue-50 border-blue-300' 
                                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                    }`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div>
                                        <div className="font-semibold text-sm">#{inv.invoiceNo}</div>
                                        <div className="text-xs text-gray-500">{inv.date}</div>
                                        {inv.autoGenerated && (
                                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mt-1">
                                            Auto Generated
                                          </span>
                                        )}
                                        {inv.isPaidInvoice && (
                                          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mt-1">
                                            Paid Invoice
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right">
                                        <div className={`font-bold text-sm ${inv.pending === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {inv.pending} PKR
                                        </div>
                                        <div className="text-xs text-gray-500">{inv.status}</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                            
                            {/* Pagination */}
                            {studentInvoices.filter(inv => 
                              inv.invoiceNo.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                              inv.date.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                            ).length > invoicesPerPage && (
                              <div className="mt-4 flex justify-center gap-2">
                                <button
                                  onClick={() => setCurrentInvoicePage(prev => Math.max(1, prev - 1))}
                                  disabled={currentInvoicePage === 1}
                                  className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Previous
                                </button>
                                <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded">
                                  {currentInvoicePage} of {Math.ceil(studentInvoices.filter(inv => 
                                    inv.invoiceNo.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                                    inv.date.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                                  ).length / invoicesPerPage)}
                                </span>
                                <button
                                  onClick={() => setCurrentInvoicePage(prev => prev + 1)}
                                  disabled={currentInvoicePage >= Math.ceil(studentInvoices.filter(inv => 
                                    inv.invoiceNo.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
                                    inv.date.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
                                  ).length / invoicesPerPage)}
                                  className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {/* Selected Invoice Display */}
                          {selectedInvoice && (
                            <div id="student-invoice-card" className="bg-gray-50 rounded-xl shadow p-4 mt-4 text-sm">
                    <div className="text-center mb-2">
                      <img src="/DSH.png" alt="DSH Logo" className="mx-auto mb-1" style={{ width: 40, height: 40, borderRadius: '50%' }} />
                      <div className="font-bold text-base mb-1">DIGITAL SKILLS HOUSE</div>
                                <div className="font-bold underline mb-2">{selectedInvoice.type} Invoice</div>
                    </div>
                    <div className="mb-2">
                                <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Invoice No:</span><span className="font-bold">{selectedInvoice.invoiceNo}</span></div>
                                <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Date:</span><span className="font-bold">{selectedInvoice.date}</span></div>
                                <div className="flex justify-between mb-1"><span className="font-bold text-gray-600">Type:</span><span className="font-bold">{selectedInvoice.type}</span></div>
                    </div>
                    <div className="mb-2 p-2 bg-gray-100 rounded">
                                <div className="font-bold text-sm mb-1">{selectedInvoice.name}</div>
                                <div className="text-gray-600 text-xs">{selectedInvoice.role}</div>
                    </div>
                    <table className="mb-2 w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td className="font-bold p-1 text-left" style={{ background: '#eff6ff' }}>Total {selectedInvoice.type}:</td>
                                    <td className="font-bold p-1 text-right" style={{ background: '#eff6ff' }}>{selectedInvoice.amount} PKR</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td className="font-bold p-1 text-left" style={{ background: '#ecfdf5' }}>Paid:</td>
                                    <td className="font-bold p-1 text-right" style={{ background: '#ecfdf5' }}>{selectedInvoice.paid} PKR</td>
                        </tr>
                        <tr>
                          <td className="font-bold p-1 text-left" style={{ background: '#fef2f2' }}>Pending:</td>
                                    <td className="font-bold p-1 text-right" style={{ background: '#fef2f2' }}>{selectedInvoice.pending} PKR</td>
                        </tr>
                      </tbody>
                    </table>
                              <div className="text-left mb-2"><span className="font-bold">Status:</span> <span className={`font-bold ${selectedInvoice.pending === 0 ? 'text-green-600' : 'text-red-600'}`}>{selectedInvoice.pending === 0 ? 'Paid' : 'Pending'}</span></div>
                    <div className="text-center mt-4 pt-2 border-t"><div className="text-xs text-gray-600">Powered by DSH Institute & Technologies</div></div>
                  </div>
                          )}
                        </div>
                )
              )}
            </div>
          )}
              </div>
            </div>
          </div>

          {/* Right Column - Notes App */}
          <div className="lg:w-1/3">
            {student && student.id ? (
              <Notes userId={student.id} userName={student.fullName} />
            ) : (
              <div className="bg-white/80 rounded-2xl shadow-xl p-6">
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading notes...</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default StudentDashboard; 